import {
  authStore as defaultAuthStore,
  type AuthPersistenceMode,
  type AuthStore,
  type SessionTokens,
} from "../../features/auth/authStore";

type HTTPMethod = "DELETE" | "GET" | "PATCH" | "POST" | "PUT";

type RequestBody = FormData | URLSearchParams | Blob | string | object;

export interface GatewayClientOptions {
  baseURL?: string;
  authStore?: AuthStore;
  fetchImpl?: typeof fetch;
}

export interface GatewayRequest {
  path: string;
  method?: HTTPMethod;
  body?: RequestBody;
  headers?: Record<string, string>;
  requireAuth?: boolean;
  signal?: AbortSignal;
}

export interface GatewayErrorPayload {
  error?: string;
  [key: string]: unknown;
}

export interface AuthLoginInput {
  identifier: string;
  password: string;
  persistence?: AuthPersistenceMode;
}

export interface AuthRegisterInput {
  email?: string;
  phone?: string;
  password: string;
  display_name: string;
  registration_disclaimer_accepted: boolean;
  registration_disclaimer_version: string;
}

export interface RegistrationConfirmationResponse {
  status: "confirmation_required";
  email: string;
  expires_at: string;
  resend_available_at: string;
}

export interface ResendRegistrationCodeInput {
  email: string;
}

export interface ConfirmRegistrationInput {
  email: string;
  code: string;
}

interface TokenPairResponse {
  access_token: string;
  refresh_token: string;
}

export class GatewayAPIError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, details?: unknown) {
    super(`gateway request failed with ${status} (${code})`);
    this.name = "GatewayAPIError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function resolveDefaultBaseURL(): string {
  const fromEnv = import.meta.env.VITE_GATEWAY_ORIGIN;
  if (typeof fromEnv === "string") {
    return trimTrailingSlash(fromEnv.trim());
  }
  return "";
}

function trimTrailingSlash(value: string): string {
  if (value.endsWith("/")) {
    return value.slice(0, -1);
  }
  return value;
}

function joinURL(baseURL: string, path: string): string {
  if (/^https?:\/\//.test(path)) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (baseURL === "") {
    return normalizedPath;
  }
  return `${baseURL}${normalizedPath}`;
}

function isJSONResponse(response: Response): boolean {
  const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";
  return contentType.includes("application/json");
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) {
    return undefined;
  }

  if (isJSONResponse(response)) {
    return response.json();
  }

  const text = await response.text();
  if (text === "") {
    return undefined;
  }
  return text;
}

function normalizeErrorCode(status: number, payload: unknown): string {
  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const maybeCode = (payload as GatewayErrorPayload).error;
    if (typeof maybeCode === "string" && maybeCode.trim() !== "") {
      return maybeCode;
    }
  }

  switch (status) {
    case 400:
      return "invalid_request";
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 405:
      return "method_not_allowed";
    case 409:
      return "conflict";
    default:
      if (status >= 500) {
        return "internal_error";
      }
      return "request_failed";
  }
}

function isRetryable401(response: Response, requireAuth: boolean, path: string): boolean {
  if (!requireAuth) {
    return false;
  }
  if (response.status !== 401) {
    return false;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return !normalizedPath.endsWith("/v1/auth/refresh");
}

function isPassthroughBody(body: RequestBody): body is FormData | URLSearchParams | Blob | string {
  return (
    typeof body === "string" ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob
  );
}

function toTokenPair(payload: unknown): SessionTokens | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const accessToken = (payload as TokenPairResponse).access_token;
  const refreshToken = (payload as TokenPairResponse).refresh_token;

  if (typeof accessToken !== "string" || typeof refreshToken !== "string") {
    return null;
  }

  if (accessToken.trim() === "" || refreshToken.trim() === "") {
    return null;
  }

  return {
    accessToken,
    refreshToken,
  };
}

function toRegistrationConfirmation(payload: unknown): RegistrationConfirmationResponse | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }

  const candidate = payload as Partial<RegistrationConfirmationResponse>;
  if (candidate.status !== "confirmation_required") {
    return null;
  }
  if (
    typeof candidate.email !== "string" ||
    typeof candidate.expires_at !== "string" ||
    typeof candidate.resend_available_at !== "string"
  ) {
    return null;
  }
  if (
    candidate.email.trim() === "" ||
    candidate.expires_at.trim() === "" ||
    candidate.resend_available_at.trim() === ""
  ) {
    return null;
  }

  return {
    status: "confirmation_required",
    email: candidate.email,
    expires_at: candidate.expires_at,
    resend_available_at: candidate.resend_available_at,
  };
}

export class GatewayAPIClient {
  private readonly baseURL: string;
  private readonly authStore: AuthStore;
  private readonly fetchImpl: typeof fetch;

  constructor(options?: GatewayClientOptions) {
    this.baseURL = trimTrailingSlash(options?.baseURL?.trim() ?? resolveDefaultBaseURL());
    this.authStore = options?.authStore ?? defaultAuthStore;
    this.fetchImpl = options?.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async request<TResponse>(input: GatewayRequest): Promise<TResponse> {
    return this.execute<TResponse>(input, false);
  }

  async get<TResponse>(path: string, options?: Omit<GatewayRequest, "method" | "path">): Promise<TResponse> {
    return this.request<TResponse>({
      ...options,
      method: "GET",
      path,
    });
  }

  async post<TResponse>(
    path: string,
    body?: RequestBody,
    options?: Omit<GatewayRequest, "method" | "path" | "body">,
  ): Promise<TResponse> {
    return this.request<TResponse>({
      ...options,
      method: "POST",
      path,
      body,
    });
  }

  async login(input: AuthLoginInput): Promise<SessionTokens> {
    const response = await this.post<TokenPairResponse>(
      "/v1/auth/login",
      {
        identifier: input.identifier,
        password: input.password,
      },
      { requireAuth: false },
    );
    const tokens = toTokenPair(response);
    if (tokens === null) {
      throw new GatewayAPIError(500, "invalid_response", response);
    }

    this.authStore.setTokens(tokens, {
      persistence: input.persistence ?? "persistent",
    });
    return tokens;
  }

  async register(input: AuthRegisterInput): Promise<RegistrationConfirmationResponse> {
    const response = await this.post<RegistrationConfirmationResponse>("/v1/auth/register", input, {
      requireAuth: false,
    });
    const confirmation = toRegistrationConfirmation(response);
    if (confirmation === null) {
      throw new GatewayAPIError(500, "invalid_response", response);
    }

    return confirmation;
  }

  async resendRegistrationCode(input: ResendRegistrationCodeInput): Promise<RegistrationConfirmationResponse> {
    const response = await this.post<RegistrationConfirmationResponse>("/v1/auth/register/resend", input, {
      requireAuth: false,
    });
    const confirmation = toRegistrationConfirmation(response);
    if (confirmation === null) {
      throw new GatewayAPIError(500, "invalid_response", response);
    }

    return confirmation;
  }

  async confirmRegistration(input: ConfirmRegistrationInput): Promise<SessionTokens> {
    const response = await this.post<TokenPairResponse>("/v1/auth/register/confirm", input, { requireAuth: false });
    const tokens = toTokenPair(response);
    if (tokens === null) {
      throw new GatewayAPIError(500, "invalid_response", response);
    }

    this.authStore.setTokens(tokens, {
      persistence: "persistent",
    });
    return tokens;
  }

  private async execute<TResponse>(input: GatewayRequest, retryAfterRefresh: boolean): Promise<TResponse> {
    const method = input.method ?? "GET";
    const requireAuth = input.requireAuth ?? true;
    const url = joinURL(this.baseURL, input.path);

    const headers: Record<string, string> = {
      ...(input.headers ?? {}),
    };

    if (requireAuth) {
      const accessToken = this.authStore.getAccessToken();
      if (accessToken !== null) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
    }

    const requestInit: RequestInit = {
      method,
      headers,
      signal: input.signal,
    };

    if (input.body !== undefined) {
      if (isPassthroughBody(input.body)) {
        requestInit.body = input.body;
      } else {
        headers["Content-Type"] = headers["Content-Type"] ?? "application/json";
        requestInit.body = JSON.stringify(input.body);
      }
    }

    const response = await this.fetchImpl(url, requestInit);

    if (!retryAfterRefresh && isRetryable401(response, requireAuth, input.path)) {
      const refreshed = await this.refreshSession();
      if (refreshed !== null) {
        return this.execute<TResponse>(input, true);
      }
      this.authStore.clear();
      this.redirectToLogin();
    }

    const payload = await parseBody(response);

    if (!response.ok) {
      throw new GatewayAPIError(response.status, normalizeErrorCode(response.status, payload), payload);
    }

    return payload as TResponse;
  }

  private async refreshSession(): Promise<SessionTokens | null> {
    const refreshToken = this.authStore.getRefreshToken();
    if (refreshToken === null) {
      return null;
    }

    let response: Response;
    try {
      response = await this.fetchImpl(joinURL(this.baseURL, "/v1/auth/refresh"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch {
      return null;
    }

    if (!response.ok) {
      return null;
    }

    const payload = await parseBody(response);
    const tokens = toTokenPair(payload);
    if (tokens === null) {
      return null;
    }

    this.authStore.setTokens(tokens);
    return tokens;
  }

  private redirectToLogin(): void {
    if (typeof window === "undefined") {
      return;
    }
    if (window.location.pathname === "/login") {
      return;
    }
    try {
      window.location.replace("/login");
    } catch {
      // Ignore navigation errors in non-browser test environments.
    }
  }
}

export function createGatewayAPIClient(options?: GatewayClientOptions): GatewayAPIClient {
  return new GatewayAPIClient(options);
}

export const gatewayAPIClient = createGatewayAPIClient();
